import type { HTMLAttributes } from 'react';
import { cn } from './cn';

export type KitStatus = 'ok' | 'warn' | 'danger' | 'info' | 'neutral';

const STATUS_CLASS: Record<KitStatus, string> = {
  ok: 'bg-ok-soft text-ok',
  warn: 'bg-warn-soft text-warn',
  danger: 'bg-danger-soft text-danger',
  info: 'bg-info-soft text-info',
  neutral: 'bg-surface-2 text-text-3',
};

interface StatusBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  status: KitStatus;
  pulse?: boolean;
}

export function StatusBadge({ status, pulse, className, children, ...props }: StatusBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-[.06em]',
        STATUS_CLASS[status],
        className
      )}
      {...props}
    >
      <span
        className={cn('h-1.5 w-1.5 rounded-full bg-current', pulse && 'animate-pulse')}
        aria-hidden="true"
      />
      {children}
    </span>
  );
}

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: 'accent' | 'neutral';
}

export function Badge({ variant = 'neutral', className, children, ...props }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-[.06em]',
        variant === 'accent' ? 'bg-accent-soft text-accent-ink' : 'bg-surface-2 text-text-2',
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
}
